/**
 * Lab assignment workspace for instructors and admins.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { assignmentService, labService } from '../../services';
import { InstructorService, UserActivityFilters, UserSummary } from '../../services/instructor';
import { Button, Card, Alert, LoadingSpinner, Input, Badge } from '../ui';
import type { Assignment, Lab } from '../../types';

interface LabAssignmentProps {
  learnerFilters?: UserActivityFilters;
  onAssignmentCreated?: () => void;
}

function userLabel(user?: Pick<UserSummary, 'display_name' | 'username' | 'email'>): string {
  if (!user) return 'Unknown learner';
  return user.display_name || user.username;
}

function assignmentUserLabel(assignment: Assignment): string {
  return assignment.user?.display_name || assignment.user?.username || 'Unknown learner';
}

export function LabAssignment({ learnerFilters, onAssignmentCreated }: LabAssignmentProps) {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedLabId, setSelectedLabId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [labSearch, setLabSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [learnersData, labsData, assignmentsData] = await Promise.all([
        InstructorService.getLearners(learnerFilters || {}),
        labService.getLabs(),
        assignmentService.getAssignments(),
      ]);

      setUsers(learnersData);
      setLabs(labsData.map(lab => ({
        ...lab,
        os: 'ubuntu-22.04',
        manifest: { rubric: [] },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })));
      setAssignments(assignmentsData || []);
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || 'Failed to load assignment data.');
    } finally {
      setLoading(false);
    }
  }, [learnerFilters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedUserIds((current) => current.filter((id) => users.some((user) => user.id === id)));
    if (selectedUserId && !users.some((user) => String(user.id) === selectedUserId)) {
      setSelectedUserId('');
    }
  }, [selectedUserId, users]);

  const filteredUsers = users.filter((user) => {
    const term = userSearch.toLowerCase();
    if (!term) return true;
    return [
      user.display_name,
      user.username,
      user.email,
      user.cohort,
      user.assigned_instructor?.display_name,
      user.assigned_instructor?.username,
    ].some((value) => value?.toLowerCase().includes(term));
  });

  const filteredLabs = labs.filter((lab) => {
    const term = labSearch.toLowerCase();
    if (!term) return true;
    return lab.title.toLowerCase().includes(term) || lab.slug.toLowerCase().includes(term);
  });

  const filteredActiveAssignments = useMemo(() => {
    const term = assignmentFilter.trim().toLowerCase();
    return assignments
      .filter((assignment) => assignment.is_active)
      .filter((assignment) => {
        if (!term) return true;
        return [
          assignment.user?.display_name,
          assignment.user?.username,
          assignment.user?.email,
          assignment.user?.cohort,
          assignment.lab?.title,
          assignment.lab?.slug,
          assignment.assigned_by?.display_name,
          assignment.assigned_by?.username,
        ].some((value) => value?.toLowerCase().includes(term));
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [assignments, assignmentFilter]);

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    );
  };

  const reloadAfterChange = async () => {
    await loadData();
    onAssignmentCreated?.();
  };

  const handleCreateAssignment = async () => {
    if (!selectedUserId || !selectedLabId) {
      setError('Select a learner and a lab.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await assignmentService.createAssignment({
        user_id: Number(selectedUserId),
        lab_id: selectedLabId,
      });
      setSelectedUserId('');
      setSelectedLabId('');
      setSuccess('Assignment created.');
      await reloadAfterChange();
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || 'Failed to create assignment.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssignment = async () => {
    if (selectedUserIds.length === 0 || !selectedLabId) {
      setError('Select at least one learner and a lab.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await assignmentService.createBulkAssignments({
        user_ids: selectedUserIds,
        lab_id: selectedLabId,
      });
      setSelectedUserIds([]);
      setSelectedLabId('');
      setSuccess(`Bulk assignment complete: ${result.created} created, ${result.skipped} skipped.`);
      if (result.errors.length > 0) {
        setError(result.errors.join(' '));
      }
      await reloadAfterChange();
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || 'Failed to create bulk assignments.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssignment = async (assignment: Assignment) => {
    const confirmed = window.confirm(
      `Unassign "${assignment.lab?.title || assignment.lab_id}" from "${assignmentUserLabel(assignment)}"?`
    );
    if (!confirmed) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await assignmentService.removeAssignment(assignment.id);
      setSuccess('Assignment removed.');
      await reloadAfterChange();
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || 'Failed to remove assignment.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className="p-6 bg-gray-800 border-gray-700">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Create Lab Assignment</h3>
            <p className="text-sm text-gray-400 mt-1">
              Uses the same learner filters as the dashboard.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setBulkMode(false)} variant={!bulkMode ? 'primary' : 'secondary'} size="sm">
              Single
            </Button>
            <Button onClick={() => setBulkMode(true)} variant={bulkMode ? 'primary' : 'secondary'} size="sm">
              Bulk
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {bulkMode ? 'Learners' : 'Learner'}
            </label>
            {!bulkMode ? (
              <select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                className="w-full p-2 border border-gray-600 rounded-md bg-gray-700 text-white"
                data-testid="user-select"
              >
                <option value="">Choose a learner...</option>
                {filteredUsers.map((learner) => (
                  <option key={learner.id} value={learner.id}>
                    {userLabel(learner)} ({learner.email})
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Filter learners..."
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                />
                <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-md bg-gray-700 p-2">
                  {filteredUsers.map((learner) => (
                    <label key={learner.id} className="flex items-center gap-2 p-1 hover:bg-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(learner.id)}
                        onChange={() => toggleUserSelection(learner.id)}
                      />
                      <span className="text-white text-sm">
                        {userLabel(learner)} ({learner.email})
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-sm text-gray-400">{selectedUserIds.length} selected</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Lab</label>
            <Input
              type="text"
              placeholder="Filter labs..."
              value={labSearch}
              onChange={(event) => setLabSearch(event.target.value)}
              className="mb-2"
            />
            <select
              value={selectedLabId}
              onChange={(event) => setSelectedLabId(event.target.value)}
              className="w-full p-2 border border-gray-600 rounded-md bg-gray-700 text-white"
              data-testid="lab-select"
            >
              <option value="">Choose a lab...</option>
              {filteredLabs.map((lab) => (
                <option key={lab.slug} value={lab.slug}>
                  {lab.title} ({lab.difficulty})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            onClick={bulkMode ? handleBulkAssignment : handleCreateAssignment}
            disabled={loading || (!bulkMode && (!selectedUserId || !selectedLabId)) || (bulkMode && (selectedUserIds.length === 0 || !selectedLabId))}
            data-testid="assign-lab-button"
          >
            {loading ? 'Creating...' : bulkMode ? 'Create Bulk Assignments' : 'Create Assignment'}
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-gray-800 border-gray-700">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Active Assignments ({filteredActiveAssignments.length})
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Current lab assignments for all learners.
            </p>
          </div>
          <Input
            type="text"
            placeholder="Filter assignments..."
            value={assignmentFilter}
            onChange={(event) => setAssignmentFilter(event.target.value)}
            className="md:w-72"
            data-testid="assignment-filter-input"
          />
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Learner</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Lab</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Assigned By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredActiveAssignments.map((assignment) => (
                <tr key={assignment.id} data-testid={`assignment-item-${assignment.id}`}>
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-white">{assignmentUserLabel(assignment)}</div>
                    <div className="text-xs text-gray-400">{assignment.user?.cohort || 'No cohort'}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-white">{assignment.lab?.title || assignment.lab_id}</div>
                    {assignment.lab?.difficulty && <Badge variant="info" size="sm">{assignment.lab.difficulty}</Badge>}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-300">
                    {assignment.assigned_by?.display_name || assignment.assigned_by?.username || 'Unknown'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-400">
                    {new Date(assignment.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <Button
                      onClick={() => handleRemoveAssignment(assignment)}
                      variant="danger"
                      size="sm"
                      data-testid={`unassign-button-${assignment.id}`}
                    >
                      Unassign
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredActiveAssignments.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              No active assignments match the current filter.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
